import { Upload } from 'antd';
import {
  ALLOW_MAX_FILE_SIZE,
  getImageURL,
  isVideoFile,
  PROFILE_IMAGE_FOLDER,
} from 'helpers/FileUploadHelper';
import { notify } from 'helpers/NotifyHelper';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { uploadMedia } from 'store/auth/actions/AuthAction';

import { ImageCropModal } from './GlobalImageUpload/ImageCropModal';

import type { UploadFile, UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';

const { Dragger } = Upload;

const CropIconSvg = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2v14a2 2 0 0 0 2 2h14" />
    <path d="M18 22V8a2 2 0 0 0-2-2H2" />
  </svg>
);

type PreviewMode = 'dragger' | 'list';

type DroppableFileUploadProps = {
  imageFolder?: string;
  uploadOnServer?: boolean;
  value?: any;
  onChange?: any;
  height?: number;
  multiple?: boolean;
  allowedTypes?: string[];
  previewMode?: PreviewMode;
  minWidth?: number;
  minHeight?: number;
  exactDimensions?: boolean;
  maxSizeMB?: number;
  handleFormValue?: any;
  maxCount?: number;
  currentUploadedCount?: number;
  uiType?: 'dragger' | 'attach';
  attachLabel?: React.ReactNode;
  /** Set false to hide the built-in file list when the parent renders its own preview. */
  showFileList?: boolean;
  /** Custom content for the empty dropzone, in place of the default 'Click or drag file here'. */
  emptyContent?: React.ReactNode;
  /** Optional crop aspect ratio, e.g. 16 / 9 */
  aspectRatio?: number;
};

const DroppableFileUpload: React.FC<DroppableFileUploadProps> = ({
  uploadOnServer = true,
  value,
  onChange,
  height = 200,
  multiple = false,
  allowedTypes = ['image/jpeg', 'image/png'],
  previewMode = 'dragger',
  imageFolder = PROFILE_IMAGE_FOLDER,
  minWidth,
  minHeight,
  exactDimensions,
  maxSizeMB = ALLOW_MAX_FILE_SIZE,
  handleFormValue,
  maxCount = 10,
  currentUploadedCount = 0,
  uiType = 'dragger',
  attachLabel = 'Attach Image',
  showFileList = true,
  emptyContent,
  aspectRatio,
}) => {
  const limitErrorShownRef = useRef(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);

  const [cropItem, setCropItem] = useState<{
    file: RcFile;
    dataUrl: string;
    onSuccess?: any;
    onError?: any;
  } | null>(null);

  const fileListRef = useRef(fileList);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  useEffect(() => {
    if (!value) {
      setFileList([]);
      return;
    }

    const values = Array.isArray(value) ? value : [value];

    const mapped: UploadFile[] = values.map((item: any, index: number) => {
      if (typeof item === 'string') {
        const isFullUrl = item.startsWith('http://') || item.startsWith('https://');
        const url = isFullUrl ? item : getImageURL(item);
        return {
          uid: `${index}`,
          name: item.split('/').pop() || `file-${index}`,
          status: 'done',
          url: url,
          response: item,
        };
      }

      return {
        uid: item.uid || `${index}`,
        name: item.name || item.url?.split('/').pop() || `file-${index}`,
        status: item.status || 'done',
        url: item.url,
        type: item.type,
        response: item.response,
        originFileObj: item.originFileObj,
      };
    });

    setFileList(mapped);
  }, [uploadOnServer, value]);

  useEffect(() => {
    return () => {
      fileListRef.current.forEach((file: any) => {
        if (file.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    async (file, fileList) => {
      // max file limit
      // if (currentUploadedCount >= maxCount) {
      //   notify.error(`Maximum ${maxCount} files allowed`);
      //   return Upload.LIST_IGNORE;
      // }
      const totalAfterSelection = currentUploadedCount + fileList.length;
      if (totalAfterSelection > maxCount) {
        // show error only once
        if (!limitErrorShownRef.current) {
          const remaingCount = maxCount - currentUploadedCount;
          if (remaingCount == 0) {
            notify.error(t('max_file_intruction_msg', { key: maxCount }));
          } else {
            notify.error(t('max_file_count_excced_error_msg', { key: remaingCount }));
          }
          limitErrorShownRef.current = true;
          setTimeout(() => {
            limitErrorShownRef.current = false;
          }, 500);
        }
        return Upload.LIST_IGNORE;
      }
      //...
      if (!allowedTypes.includes(file.type)) {
        notify.error(t('allowed_file_type_error_msg', { key: allowedTypes.join(', ') }));
        return Upload.LIST_IGNORE;
      }

      const fileSizeMB = file.size / 1024 / 1024;
      if (maxSizeMB && fileSizeMB > maxSizeMB) {
        notify.error(t('file_size_excced_error_msg', { key: maxSizeMB }));
        return Upload.LIST_IGNORE;
      }

      if (file.type.startsWith('image/') && (minWidth || minHeight)) {
        const isValid = await new Promise<boolean>((resolve) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file as RcFile);

          img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const width = img.width;
            const height = img.height;

            if (exactDimensions && minWidth && minHeight) {
              if (width !== minWidth || height !== minHeight) {
                notify.error(
                  t('exact_image_dimension_error_msg', {
                    width: minWidth,
                    height: minHeight,
                  })
                );
                return resolve(false);
              }
            } else {
              const valid = (!minWidth || width >= minWidth) && (!minHeight || height >= minHeight);

              if (!valid) {
                notify.error(
                  t('min_image_dimension_error_msg', {
                    width: minWidth,
                    height: minHeight,
                  })
                );
                return resolve(false);
              }
            }

            resolve(true);
          };

          img.onerror = () => {
            notify.error(t('invalid_error_msg', { field_name: t('file') }));

            resolve(false);
          };
          img.src = objectUrl;
        });

        if (!isValid) return Upload.LIST_IGNORE;
      }

      if (!uploadOnServer) {
        return false;
      }

      return true;
    },
    [
      currentUploadedCount,
      maxCount,
      allowedTypes,
      maxSizeMB,
      minWidth,
      minHeight,
      uploadOnServer,
      exactDimensions,
    ]
  );

  const proceedWithUpload = useCallback(
    async (fileToUpload: File | RcFile, onSuccess?: any, onError?: any) => {
      try {
        setLoading(true);

        const formData = new FormData();
        formData.append('path', imageFolder);
        formData.append('file', fileToUpload);

        const response: any = await uploadMedia(formData);

        if (!response.status) throw new Error(t('file_upload_failed_error_msg'));

        const responseData = response.data;

        const data = {
          links: responseData.links,
          url: getImageURL(responseData.links),
        };

        handleFormValue?.(responseData.links);
        onSuccess?.(data, fileToUpload as RcFile);
      } catch (err) {
        notify.error(t('file_upload_failed_error_msg'));
        onError?.(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [imageFolder, handleFormValue]
  );

  const customRequest = useCallback<NonNullable<UploadProps['customRequest']>>(
    async ({ file, onSuccess, onError }) => {
      if (
        !multiple &&
        (file as RcFile).type?.startsWith('image/') &&
        (file as RcFile).type !== 'image/svg+xml'
      ) {
        const blobUrl = URL.createObjectURL(file as RcFile);
        setCropItem({
          file: file as RcFile,
          dataUrl: blobUrl,
          onSuccess,
          onError,
        });
        return;
      }

      await proceedWithUpload(file as RcFile, onSuccess, onError);
    },
    [multiple, proceedWithUpload]
  );

  const handleCropComplete = async (croppedFile: File, _croppedDataUrl: string) => {
    const currentCrop = cropItem;
    setCropItem(null);
    if (!currentCrop) return;

    if (uploadOnServer) {
      if (currentCrop.onSuccess) {
        await proceedWithUpload(croppedFile, currentCrop.onSuccess, currentCrop.onError);
      } else {
        // This is a re-crop of an existing file in the list.
        await proceedWithUpload(croppedFile, (data: any, uploadedFile: RcFile) => {
          // Update the file in the fileList
          setFileList((prev) => {
            const newList = [...prev];
            const idx = newList.findIndex((f) => f.uid === currentCrop.file.uid);
            if (idx > -1) {
              newList[idx] = {
                ...newList[idx],
                url: data.url,
                response: { ...newList[idx].response, links: data.links, url: data.url },
                originFileObj: uploadedFile,
              };
            }
            return newList;
          });
          // Call onChange with the updated list or single item
          onChange?.(
            multiple
              ? fileList // We might need to map properly, but we'll stick to updating the single item
              : {
                  ...currentCrop.file,
                  url: data.url,
                  links: data.links,
                  originFileObj: uploadedFile,
                }
          );
        });
      }
    } else {
      // Offline crop (uploadOnServer = false)
      const previewUrl = URL.createObjectURL(croppedFile);
      const newRcFile = croppedFile as RcFile;
      newRcFile.uid = currentCrop.file.uid;

      if (currentCrop.onSuccess) {
        // If it somehow opened during initial customRequest flow, complete it locally
        currentCrop.onSuccess({ url: previewUrl, links: '' }, newRcFile);
      } else {
        // Re-crop of existing file in the list
        setFileList((prev) => {
          const newList = [...prev];
          const idx = newList.findIndex((f) => f.uid === currentCrop.file.uid);
          if (idx > -1) {
            newList[idx] = {
              ...newList[idx],
              url: previewUrl,
              preview: previewUrl,
              originFileObj: newRcFile,
            };
          }
          return newList;
        });

        const formattedObj = {
          ...currentCrop.file,
          uid: currentCrop.file.uid,
          name: newRcFile.name || currentCrop.file.name,
          type: newRcFile.type || currentCrop.file.type,
          size: currentCrop.file.size,
          url: previewUrl,
          preview: previewUrl,
          originFileObj: newRcFile,
        };

        onChange?.(multiple ? [formattedObj] : formattedObj);
      }
    }
  };

  const handleEditCrop = (file: UploadFile) => {
    const src =
      file.url ||
      file.response?.url ||
      (file.originFileObj && URL.createObjectURL(file.originFileObj));
    if (!src) return;

    const rcFile =
      file.originFileObj || new File([], file.name || 'image.jpg', { type: 'image/jpeg' });
    // mock uid if missing to replace correctly
    (rcFile as any).uid = file.uid;

    setCropItem({
      file: rcFile as RcFile,
      dataUrl: src,
    });
  };

  const handleChange = useCallback<NonNullable<UploadProps['onChange']>>(
    (info) => {
      let newFileList = info.fileList;

      if (!multiple) {
        newFileList = newFileList.slice(-1);
      }

      newFileList = newFileList.map((file) => {
        if (!file.url && !file.preview && file.originFileObj) {
          file.preview = URL.createObjectURL(file.originFileObj);
        }
        return file;
      });

      setFileList(newFileList);

      const formatted = newFileList
        .map((file: any) => {
          const url = file.response?.url || file.url || file.preview;

          if (!url) return null;

          return {
            ...file,
            url,
            links: file.response?.links || '',
          };
        })
        .filter(Boolean);

      onChange?.(multiple ? formatted : formatted[0] || null);
    },
    [multiple, onChange]
  );

  const handleRemove = useCallback(
    (file: UploadFile) => {
      if ((file as any).preview?.startsWith('blob:')) {
        URL.revokeObjectURL((file as any).preview);
      }

      const updatedList = fileList.filter((f) => f.uid !== file.uid);
      setFileList(updatedList);

      onChange?.(multiple ? updatedList : null);
    },
    [fileList, multiple, onChange]
  );

  const handlePreview = (file: UploadFile) => {
    const src =
      file.url ||
      file.response?.url ||
      (file.originFileObj && URL.createObjectURL(file.originFileObj));

    if (!src) return;

    // const image = new Image();
    // image.src = src;

    // const newWindow = window.open(src);
    // newWindow?.document.write(image.outerHTML);
    window.open(src, '_blank');
  };

  const listType = useMemo(() => {
    if (previewMode === 'list') return 'text';
    const onlyImages = fileList.every((f) => f.type?.startsWith('image/'));
    return onlyImages ? 'picture-card' : 'text';
  }, [fileList, previewMode]);

  const acceptTypes = useMemo(() => allowedTypes.join(','), [allowedTypes]);

  return (
    <>
      <Dragger
        name="file"
        multiple={multiple}
        accept={acceptTypes}
        beforeUpload={beforeUpload}
        customRequest={uploadOnServer ? customRequest : undefined}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={handleRemove}
        fileList={fileList}
        maxCount={maxCount}
        // disabled={currentUploadedCount >= maxCount}
        /* showUploadList={previewMode === 'list'} */
        showUploadList={
          previewMode === 'list' && showFileList
            ? {
                showRemoveIcon: true,
              }
            : false
        }
        listType={listType}
        style={{
          height: uiType === 'dragger' && previewMode === 'dragger' ? height : undefined,
          padding: 0,
          overflow: 'hidden',
          border: uiType === 'attach' ? 'none' : undefined,
          background: uiType === 'attach' ? 'transparent' : undefined,
        }}
        className={`${uiType === 'attach' ? 'no-padding-upload' : ''}`}
      >
        {previewMode === 'dragger' && fileList.length > 0 ? (
          <div className="row g-2 p-2 h-100">
            {fileList.map((file: any) => {
              const src = file.response?.url || file.url || file.preview;

              // a blob: URL says nothing about the media type, so check the file's own type first
              // or a freshly picked video lands in an <img> and renders blank
              const fileType: string = file.type || file.originFileObj?.type || '';
              const isVideo = fileType.startsWith('video/') || (!!src && isVideoFile(src));

              const isImage =
                !isVideo &&
                (fileType.startsWith('image/') ||
                  src?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ||
                  src?.startsWith('blob:') ||
                  src?.startsWith('data:image/'));

              return (
                <div key={file.uid} className="col-6 col-sm-3 position-relative image-item-wrapper">
                  {isImage ? (
                    <img src={src} className="img-fluid rounded" alt={file.name} />
                  ) : isVideo ? (
                    // #t=0.1 makes the browser paint the first frame as the thumbnail
                    <video
                      src={`${src}#t=0.1`}
                      className="img-fluid rounded"
                      preload="metadata"
                      muted
                      playsInline
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center rounded border"
                      style={{ height: 100 }}
                    >
                      <span className="text-truncate px-2">{file.name || t('file')}</span>
                    </div>
                  )}
                  {isImage && !multiple && file.type !== 'image/svg+xml' && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCrop(file);
                      }}
                      style={{
                        position: 'absolute',
                        top: '-6%',
                        left: 0,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: '#000',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('crop_image')}
                    >
                      <CropIconSvg />
                    </button>
                  )}
                  <button
                    type="button"
                    className="close-btn btn btn-sm banner-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(file);
                    }}
                  />
                </div>
              );
            })}
          </div>
        ) : uiType === 'attach' ? (
          <div>
            <>{loading ? <span>Uploading...</span> : attachLabel}</>
          </div>
        ) : (
          fileList.length === 0 &&
          (emptyContent ? (
            <div className="droppable-upload-empty">{loading ? 'Uploading...' : emptyContent}</div>
          ) : (
            <div style={{ lineHeight: `${height}px` }}>
              {loading ? 'Uploading...' : 'Click or drag file here'}
            </div>
          ))
        )}
      </Dragger>

      {cropItem && (
        <ImageCropModal
          isOpen={!!cropItem}
          imageSrc={cropItem.dataUrl}
          fileName={cropItem.file.name || 'image.jpg'}
          aspectRatio={aspectRatio}
          onClose={() => setCropItem(null)}
          onCropComplete={handleCropComplete}
        />
      )}
    </>
  );
};

export default DroppableFileUpload;
